import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useMemo, useRef, useState } from "react";
import { useParams } from "react-router";

import {
  ActorStatus,
  ActorVoiceStatus,
  GenerateJobClipRequestType,
  JobClipType,
  type JobDetailClipListItemDto,
  type JobQuestionAnswerDto,
  type JobStatus,
  TextLlm,
} from "Infrastructure/Api/Api";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import type { ModuleQuestionDto } from "Modules/Api/Queries/useModuleQuestionsQuery";
import { useMediaFilesQuery } from "MusicAudio/Api/Queries/useMediaFilesQuery";
import { useGenerateClipMutation } from "Projects/Api/Mutations/useGenerateClipMutation";
import { useGenerateVoicelinesMutation } from "Projects/Api/Mutations/useGenerateVoicelinesMutation";
import { useProjectActorVoicesQuery } from "Projects/Api/Queries/useProjectActorVoicesQuery";
import { useProjectDetailQuery } from "Projects/Api/Queries/useProjectDetailQuery";
import { AiDraftPanel } from "Projects/Components/AiDraftPanel";
import { AiRefinementPanel } from "Projects/Components/AiRefinementPanel";
import {
  JobDetailSidebar,
  TAB_IDS,
} from "Projects/Components/JobDetailSidebar";
import {
  JobQuestionnaireForm,
  type JobQuestionnaireFormHandle,
} from "Projects/Components/JobQuestionnaireForm";
import { LlmModelSelect } from "Projects/Components/LlmModelSelect";
import { ScriptEditor } from "Projects/Components/ScriptEditor/ScriptEditor";
import {
  createVoiceSegment,
  fromJobScriptFE,
  useScriptEditor,
} from "Projects/Components/ScriptEditor/ScriptEditorContext";
import { useScriptEstimation } from "Projects/Hooks/useScriptEstimation";
import { formatDuration } from "Projects/Utils/formatDuration";
import { isJobLocked } from "Projects/Utils/jobStatusUtils";
import { splitIntoParagraphs } from "Projects/Utils/splitIntoParagraphs";
import type { ScriptValidationError } from "Projects/Utils/validateScript";
import { Price } from "Shared/Price/Price";

export type WizardMode = "choice" | "questionnaire" | "draft" | "script";

interface JobCreationWizardProps {
  mode: WizardMode;
  onModeChange: (mode: WizardMode) => void;
  moduleQuestions?: ModuleQuestionDto[];
  existingAnswers?: JobQuestionAnswerDto[] | null;
  validationErrors?: ScriptValidationError[];
  jobStatus: JobStatus;
  clips: JobDetailClipListItemDto[];
}

export const JobCreationWizard = ({
  mode,
  onModeChange,
  moduleQuestions,
  existingAnswers,
  validationErrors = [],
  jobStatus,
  clips,
}: JobCreationWizardProps): React.ReactNode => {
  const { t } = useResource();
  const theme = useTheme();
  const { projectId, jobId } = useParams<{
    projectId: string;
    jobId: string;
  }>();

  const { setScript, script, audioOutputOptions, uiDefaults, isEditable } =
    useScriptEditor();
  const { data: actorVoices } = useProjectActorVoicesQuery(projectId!);
  const { data: project } = useProjectDetailQuery(projectId!);
  const { data: mediaFiles } = useMediaFilesQuery({ limit: 50 });

  const mediaFileNames = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    mediaFiles?.items.forEach(
      (f: {
        partnerMediaFileID: string;
        displayName?: string | null;
        fileName: string;
      }) => {
        map[f.partnerMediaFileID] = f.displayName ?? f.fileName;
      },
    );
    return map;
  }, [mediaFiles]);

  const { estimatedDurationSeconds, estimatedPrice } = useScriptEstimation({
    segments: script.segments,
    globalSpeedFactor: script.globalSpeedFactor,
    project,
    actorVoices,
    language: project?.language,
  });

  // For done jobs, show the finalized clip price instead of the estimate
  const finalClip = clips.find((c) => c.type === JobClipType.Final);
  const displayPrice =
    !isEditable && finalClip ? finalClip.price : estimatedPrice;

  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [isGeneratingClip, setIsGeneratingClip] = useState(false);
  const [sidebarTab, setSidebarTab] = useState<number>(
    isJobLocked(jobStatus) ? TAB_IDS.Finalize : TAB_IDS.GeneralSettings,
  );
  const [usesQuestions, setUsesQuestions] = useState(
    () => (existingAnswers?.length ?? 0) > 0,
  );
  const [initialArtifact, setInitialArtifact] = useState("");
  const [isQuestionnaireGenerating, setIsQuestionnaireGenerating] =
    useState(false);
  const [questionnaireModel, setQuestionnaireModel] = useState<TextLlm>(
    TextLlm.OpenAI_ChatGpt5,
  );

  const generateClipMutation = useGenerateClipMutation(projectId!, jobId!);
  const generateVoicelinesMutation = useGenerateVoicelinesMutation(
    projectId!,
    jobId!,
  );

  const questionnaireRef = useRef<JobQuestionnaireFormHandle>(null);

  const hasQuestions = (moduleQuestions?.length ?? 0) > 0;

  const handleBeforeGenerate = async (): Promise<boolean> => {
    if (!questionnaireRef.current) return true;
    return questionnaireRef.current.saveIfDirty();
  };

  const handleQuestionnaireGenerate = async () => {
    if (!questionnaireRef.current) return;

    const saved = await questionnaireRef.current.validateAndSave();
    if (!saved) return;

    setIsQuestionnaireGenerating(true);
    try {
      const result = await generateVoicelinesMutation.mutateAsync({
        textLlm: questionnaireModel,
        instructions:
          "Generate the voiceover text based on the provided briefing answers.",
        context: undefined,
      });
      setInitialArtifact(result.generatedContent);
      onModeChange("draft");
    } catch {
      // error handled by mutation
    } finally {
      setIsQuestionnaireGenerating(false);
    }
  };

  const handleConvertToScript = (text: string) => {
    if (text.trim()) {
      const defaultVoiceID =
        uiDefaults.defaultActorVoiceID ??
        actorVoices?.find(
          (av) =>
            av.actorStatus === ActorStatus.Active &&
            av.voiceStatus === ActorVoiceStatus.Active,
        )?.actorVoiceID ??
        "";
      const paragraphs = splitIntoParagraphs(text);
      const segments = paragraphs.map((p) =>
        createVoiceSegment({
          actorVoiceID: defaultVoiceID,
          languageOverride: null,
          speedFactor: 1.0,
          volumePercent: 100,
          spans: [{ text: p, style: null }],
        }),
      );
      setScript({
        variables: [],
        segments,
        globalSpeedFactor: 1.0,
        globalVolumePercent: 100,
        defaultActorVoiceID: defaultVoiceID || null,
        defaultLanguage: null,
      });
    }
    onModeChange("script");
  };

  const handleGeneratePreview = async () => {
    setIsGeneratingPreview(true);
    setSidebarTab(TAB_IDS.Finalize);
    try {
      await generateClipMutation.mutateAsync({
        type: GenerateJobClipRequestType.Preview,
        script: fromJobScriptFE(script, audioOutputOptions),
      });
    } catch {
      // auto-toast shown by mutation hook
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  const handleGenerateClip = async () => {
    setIsGeneratingClip(true);
    setSidebarTab(TAB_IDS.Finalize);
    try {
      await generateClipMutation.mutateAsync({
        type: GenerateJobClipRequestType.Full,
        script: fromJobScriptFE(script, audioOutputOptions),
      });
    } catch {
      // auto-toast shown by mutation hook
    } finally {
      setIsGeneratingClip(false);
    }
  };

  const showQuestionsSidebar = usesQuestions && hasQuestions;

  return (
    <Stack spacing={Spacing.M}>
      {/* Choice screen — only when module has questions and user hasn't chosen yet */}
      {mode === "choice" && hasQuestions && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            py: Spacing.L,
          }}
        >
          <Typography variant="h6" fontWeight={600} mb={1}>
            {t(Resources.JobDetail.ChoiceScreen.Title)}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={Spacing.M}>
            {t(Resources.JobDetail.ChoiceScreen.Description)}
          </Typography>

          <Stack
            direction="row"
            spacing={Spacing.M}
            sx={{ width: "100%", maxWidth: 700 }}
          >
            <Box
              onClick={() => {
                setUsesQuestions(true);
                onModeChange("questionnaire");
              }}
              sx={{
                flex: 1,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: Radius.XL,
                p: Spacing.M,
                cursor: "pointer",
                textAlign: "center",
                transition: "border-color 0.15s, background-color 0.15s",
                "&:hover": {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: `${theme.palette.primary.main}10`,
                },
              }}
            >
              <QuizOutlinedIcon
                sx={{ fontSize: 40, color: theme.palette.primary.main, mb: 1 }}
              />
              <Typography variant="body1" fontWeight={600} mb={0.5}>
                {t(Resources.JobDetail.ChoiceScreen.AnswerQuestionsTitle)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t(Resources.JobDetail.ChoiceScreen.AnswerQuestionsDescription)}
              </Typography>
            </Box>

            <Box
              onClick={() => {
                setUsesQuestions(false);
                onModeChange("draft");
              }}
              sx={{
                flex: 1,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: Radius.XL,
                p: Spacing.M,
                cursor: "pointer",
                textAlign: "center",
                transition: "border-color 0.15s, background-color 0.15s",
                "&:hover": {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: `${theme.palette.primary.main}10`,
                },
              }}
            >
              <EditNoteOutlinedIcon
                sx={{ fontSize: 40, color: theme.palette.primary.main, mb: 1 }}
              />
              <Typography variant="body1" fontWeight={600} mb={0.5}>
                {t(Resources.JobDetail.ChoiceScreen.OwnPromptTitle)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t(Resources.JobDetail.ChoiceScreen.OwnPromptDescription)}
              </Typography>
            </Box>
          </Stack>
        </Box>
      )}

      {/* Questionnaire — centered */}
      {mode === "questionnaire" && moduleQuestions && (
        <Box
          sx={{
            maxWidth: 800,
            width: "100%",
            alignSelf: "center",
          }}
        >
          <Box
            sx={{
              border: `1px solid ${theme.colors.border}`,
              borderRadius: "12px",
              padding: theme.spacing(Spacing.M),
            }}
          >
            <JobQuestionnaireForm
              ref={questionnaireRef}
              questions={moduleQuestions}
              existingAnswers={existingAnswers}
            />

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mt={Spacing.M}
            >
              <Button
                variant="text"
                color="secondary"
                size="small"
                onClick={() => onModeChange("choice")}
              >
                {t(Resources.JobDetail.Questionnaire.Back)}
              </Button>
              <Stack direction="row" spacing={1} alignItems="center">
                <LlmModelSelect
                  value={questionnaireModel}
                  onChange={setQuestionnaireModel}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleQuestionnaireGenerate}
                  disabled={isQuestionnaireGenerating}
                >
                  {isQuestionnaireGenerating ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={16} color="inherit" />
                      <span>
                        {t(Resources.JobDetail.Questionnaire.GeneratingDraft)}
                      </span>
                    </Stack>
                  ) : (
                    t(Resources.JobDetail.Questionnaire.Generate)
                  )}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Box>
      )}

      {/* Draft — with optional questions sidebar */}
      {mode === "draft" && (
        <Grid container spacing={Spacing.M}>
          {showQuestionsSidebar && moduleQuestions && (
            <Grid size={4}>
              <Box
                sx={{
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: "12px",
                  padding: theme.spacing(Spacing.M),
                  position: "sticky",
                  top: theme.spacing(Spacing.M),
                  display: "flex",
                  flexDirection: "column",
                  gap: theme.spacing(Spacing.M),
                }}
              >
                <JobQuestionnaireForm
                  ref={questionnaireRef}
                  questions={moduleQuestions}
                  existingAnswers={existingAnswers}
                />
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleQuestionnaireGenerate}
                  disabled={isQuestionnaireGenerating}
                >
                  {isQuestionnaireGenerating ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={16} color="inherit" />
                      <span>
                        {t(Resources.JobDetail.Questionnaire.GeneratingDraft)}
                      </span>
                    </Stack>
                  ) : (
                    t(Resources.JobDetail.RegenerateFromQuestionnaire)
                  )}
                </Button>
              </Box>
            </Grid>
          )}
          <Grid size={showQuestionsSidebar ? 8 : 12}>
            <Box
              sx={{
                border: `1px solid ${theme.colors.border}`,
                borderRadius: "12px",
                padding: theme.spacing(Spacing.M),
              }}
            >
              <AiDraftPanel
                onConvertToScript={handleConvertToScript}
                onSkipToManual={() => onModeChange("script")}
                onBeforeGenerate={
                  showQuestionsSidebar ? handleBeforeGenerate : undefined
                }
                initialArtifact={initialArtifact || undefined}
                hasQuestionnaire={showQuestionsSidebar}
              />
            </Box>
          </Grid>
        </Grid>
      )}

      {/* Script editor */}
      {mode === "script" && (
        <Grid container spacing={Spacing.M}>
          <Grid size={8}>
            <Box
              sx={{
                border: `1px solid ${theme.colors.border}`,
                borderRadius: "12px",
                padding: theme.spacing(Spacing.M),
              }}
            >
              <Stack spacing={Spacing.M}>
                {isEditable && (
                  <AiRefinementPanel
                    onBackToDraft={() => onModeChange("draft")}
                  />
                )}

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body1" fontSize={15} fontWeight={600}>
                    {t(Resources.JobDetail.PrepareYourVoicelines)}
                  </Typography>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontSize={13}
                    >
                      {t(Resources.JobDetail.ExpectedLength)}:{" "}
                      {formatDuration(estimatedDurationSeconds)}
                    </Typography>
                    {displayPrice !== null && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontSize={13}
                      >
                        {t(Resources.JobDetail.ExpectedPrice)}:{" "}
                        <Price amount={displayPrice} />
                      </Typography>
                    )}
                  </Stack>
                </Box>

                <ScriptEditor
                  actorVoices={actorVoices}
                  validationErrors={validationErrors}
                  mediaFileNames={mediaFileNames}
                />

                {isEditable && (
                  <Grid container spacing={Spacing.S}>
                    <Grid size={6}>
                      <Button
                        variant="outlined"
                        color="primary"
                        fullWidth
                        onClick={handleGeneratePreview}
                        disabled={isGeneratingPreview || isGeneratingClip}
                      >
                        {isGeneratingPreview ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          t(Resources.JobDetail.GeneratePreview)
                        )}
                      </Button>
                    </Grid>
                    <Grid size={6}>
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={handleGenerateClip}
                        disabled={isGeneratingPreview || isGeneratingClip}
                      >
                        {isGeneratingClip ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          t(Resources.JobDetail.GenerateFullVoiceover)
                        )}
                      </Button>
                    </Grid>
                  </Grid>
                )}
              </Stack>
            </Box>
          </Grid>
          <Grid size={4}>
            <Box
              sx={{
                border: `1px solid ${theme.colors.border}`,
                borderRadius: "12px",
              }}
            >
              <JobDetailSidebar
                projectID={projectId!}
                jobID={jobId!}
                jobStatus={jobStatus}
                clips={clips}
                activeTabID={sidebarTab}
                onTabChange={setSidebarTab}
              />
            </Box>
          </Grid>
        </Grid>
      )}
    </Stack>
  );
};
